<template>
  <div
    style="height: 100%; display: flex; flex-direction: column; overflow: hidden;"
    :class="theme.isDark ? 'black' : 'grey lighten-5'"
  >
    <portal to="dashboard-title">
      {{ dashboardTitle }}
    </portal>
    <portal to="dashboard-footer">
      <span class="headline font-weight-medium">
        {{ now }}
      </span>
    </portal>

    <DashboardLoading v-if="loading || !initialized || !activeSite" />

    <v-container
      v-else
      fluid
      class="pa-0"
      v-resize="onResize"
      :class="theme.isDark ? '' : 'grey lighten-5'"
      style="flex-grow: 1; overflow-y: hidden;"
    >
      <div
        style="margin: 0 auto;"
        :style="`height: ${height}px; width: ${width}px;`"
      >
        <v-row v-if="initialized && activeSite" style="height: 100%;">
          <v-col cols="8" style="height: 100%; padding: 14px 8px 0px 14px;">
            <v-row no-gutters style="height: 33.33333%; margin: 0px -4px 0px;">
              <v-col cols="6" style="height: 100%; padding: 4px;">
                <!-- Key applied here to force 60s Live Data Refetch -->
                <MachineDowntimeToday
                  :key="`live-${liveRefreshKey}`"
                  class="w-full h-full"
                  widgettitle="Live"
                  :scale="scale"
                />
              </v-col>
              <v-col cols="6" style="height: 100%; padding: 4px;">
                <MachineDowntimeYesterday class="w-full h-full"
                 widgettitle="Yesterday (MINS)" :scale="scale" />
              </v-col>
            </v-row>
            <v-row no-gutters style="height: 33.33333%; margin: 0px -4px 0px;">
              <v-col cols="6" style="height: 100%; padding: 4px;">
                <MachineDowntimeMonth
                  class="w-full h-full"
                  widgettitle="Last 30 days"
                  :scale="scale"
                />
              </v-col>
              <v-col cols="6" style="height: 100%; padding: 4px;">
                <DeptYesterday
                  class="w-full h-full"
                  widgettitle="Dept Yesterday"
                  :scale="scale"
                />
              </v-col>
            </v-row>
            <v-row no-gutters style="height: 33.33333%; margin: 0px -4px 0px;">
              <v-col cols="6" style="height: 100%; padding: 4px;">
                <DeptMonth
                  class="w-full h-full"
                  widgettitle="Dept 30 Days"
                  :scale="scale"
                />
              </v-col>
              <v-col cols="6" style="height: 100%; padding: 4px;">
                <DeptWeek
                  class="w-full h-full"
                  widgettitle="Dept Last 7 days"
                  :scale="scale"
                />
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="4" style="height: 100%; padding: 14px 16px 0px 0px;">
            <MachineDowntimeWeek
              class="w-full h-full"
              widgettitle="Machine Last 7 days"
              :scale="scale"
            />
          </v-col>
        </v-row>
      </div>
    </v-container>

    <v-app-bar
      flat
      v-if="isTV || isFullscreen"
      :color="$vuetify.theme.dark ? '#121212' : 'grey lighten-5'"
    >
      <img
        :src="`/assets/logo/${logoName}.png`"
        contain
        :alt="altText"
        :height="logoSize"
      />
      <v-toolbar-title
        :class="$vuetify.breakpoint.mdAndUp ? 'headline font-weight-medium ml-4' : 'title pl-0'"
      >
        <portal-target name="dashboard-title"></portal-target>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <portal-target name="dashboard-footer"></portal-target>
    </v-app-bar>
  </div>
</template>

<script>
import {
  mapState,
  mapMutations,
  mapActions,
  mapGetters,
} from 'vuex';

import DeptMonth from '../components/widgets/DeptMonth.vue';
import DeptWeek from '../components/widgets/DeptWeek.vue';
import DeptYesterday from '../components/widgets/DeptYesterday.vue';
import MachineDowntimeMonth from '../components/widgets/MachineDowntimeMonth.vue';
import MachineDowntimeToday from '../components/widgets/MachineDowntimeToday.vue';
import MachineDowntimeYesterday from '../components/widgets/MachineDowntimeYesterday.vue';
import MachineDowntimeWeek from '../components/widgets/MachineDowntimeWeek.vue';
import DashboardLoading from '../components/DashboardLoading.vue';

export default {
  name: 'cellwiseDowntimeDashboard',
  components: {
    DeptMonth,
    DeptWeek,
    DeptYesterday,
    MachineDowntimeMonth,
    MachineDowntimeToday,
    MachineDowntimeWeek,
    MachineDowntimeYesterday,
    DashboardLoading,
  },
  inject: ['theme'],
  data() {
    return {
      scale: 0,
      width: 0,
      height: 0,
      ratio: 16 / 9,
      loading: false,
      initialized: false,
      dashboardBootstrapped: false,
      dashboardUserKey: '',
      now: '',
      currentimeinterval: null,
      liveRefreshKey: 0,
    };
  },
  computed: {
    ...mapState('cellwiseDowntimeDashboard', ['serverTime', 'config', 'userId', 'customerId', 'username', 'theme', 'selectedCell']),
    ...mapGetters('helper', ['primaryLogo', 'primaryText ', 'isTV']),
    ...mapState('helper', ['isFullscreen']),
    ...mapGetters('cellwiseDowntimeDashboard', ['activeSite']),

    routeCellName() {
      return this.$route.query.selectedcell || this.$route.query.cellname || '';
    },

    dashboardTitle() {
      if (this.routeCellName && this.config && this.config.dashboardTitle) {
        return `${this.config.dashboardTitle} ${this.routeCellName}`;
      }

      if (this.routeCellName) {
        return `Downtime Dashboard - ${this.routeCellName}`;
      }

      return 'Downtime Dashboard';
    },

    logoName() {
      if (!this.primaryLogo) return 'shopworx-dark';
      return this.$vuetify.theme.dark
        ? `${this.primaryLogo.name}-dark`
        : `${this.primaryLogo.name}-light`;
    },

    logoSize() {
      return this.primaryLogo ? this.primaryLogo.size : 40;
    },

    altText() {
      return this.primaryLogo ? `${this.primaryLogo.name} logo` : 'Shopworx logo';
    },

    showFooter() {
      return this.footer?.display;
    },
  },
  watch: {
    $route: {
      immediate: true,
      async handler(val) {
        const query = val ? val.query : {};
        const routeCellName = query.selectedcell || query.cellname;
        const nextQuery = { ...query };
        let shouldReplaceQuery = false;

        if (routeCellName && query.selectedcell !== routeCellName) {
          nextQuery.selectedcell = routeCellName;
          shouldReplaceQuery = true;
        }

        let urlTheme = query.theme;

        if (urlTheme !== 'dark' && urlTheme !== 'light') {
          const localDark = localStorage.getItem('dark');
          urlTheme = (localDark === 'false' || localDark === false) ? 'light' : 'dark';
        }

        if (query.theme !== urlTheme) {
          nextQuery.theme = urlTheme;
          shouldReplaceQuery = true;
        }

        if (shouldReplaceQuery) {
          this.$router.replace({ query: nextQuery }).catch(() => {});
          return;
        }

        const isDark = urlTheme === 'dark';

        this.$vuetify.theme.dark = isDark;
        this.applyThemeColors(isDark);

        if (this.theme && this.theme.isDark !== isDark) {
          this.setTheme({ ...this.theme, isDark });
        }

        const userKey = `${query.userid || ''}|${query.customerid || ''}|${query.username || ''}`;
        if (!this.dashboardBootstrapped || this.dashboardUserKey !== userKey) {
          this.dashboardUserKey = userKey;
          await this.initializeDashboard(query);
        }
      },
    },

    'theme.isDark': {
      handler(newIsDark) {
        this.$vuetify.theme.dark = newIsDark;
        this.applyThemeColors(newIsDark);

        const themeStr = newIsDark ? 'dark' : 'light';
        localStorage.setItem('dark', newIsDark);

        if (this.$route.query.theme !== themeStr) {
          const newQuery = { ...this.$route.query, theme: themeStr };
          this.$router.replace({ query: newQuery }).catch(() => {});
        }
      },
    },

    activeSite(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        console.log('[Component Log] Shopworx Shell hydrated user session. Unlocking Dashboard.');
        this.initialized = true;
      }
    },

    serverTime() {
      this.setTime();
    },
  },
  async created() {
    await this.getMe();
  },
  mounted() {
    if (this.theme && this.theme.themes) {
      const activeTheme = this.theme.isDark ? 'dark' : 'light';
      this.$vuetify.theme.dark = this.theme.isDark;

      setTimeout(() => {
        this.onResize();
      }, 50);

      const customColors = this.theme.themes[activeTheme];
      Object.entries(customColors).forEach(([key, value]) => {
        this.$vuetify.theme.themes[activeTheme][key] = value;
      });
    }
    this.onResize();
    this.setTime();

    this.currentimeinterval = setInterval(async () => {
      await this.getBusinessTime();
      this.setTime();
      this.liveRefreshKey += 1;
    }, 60000);
  },
  destroyed() {
    clearInterval(this.currentimeinterval);
  },
  methods: {
    ...mapMutations('cellwiseDowntimeDashboard', [
      'setUserId',
      'setCustomerId',
      'setUsername',
      'setDataFromJSON',
      'setConfigJson',
      'setIntervalFetch',
      'setSiteId',
      'setLoading',
      'setTheme',
    ]),
    ...mapActions('user', ['getMe']),
    ...mapActions('cellwiseDowntimeDashboard', ['getFileData', 'getMachines', 'getBusinessTime', 'allShifts', 'fetchDowntimeReasons']),

    applyThemeColors(isDark) {
      if (!this.theme || !this.theme.themes) return;

      const activeTheme = isDark ? 'dark' : 'light';
      this.$vuetify.theme.dark = isDark;

      const customColors = this.theme.themes[activeTheme];
      Object.entries(customColors).forEach(([key, value]) => {
        this.$vuetify.theme.themes[activeTheme][key] = value;
      });
    },

    async initializeDashboard(query) {
      this.loading = true;
      this.initialized = false;
      this.dashboardBootstrapped = true;

      const { userid, customerid, username } = query;
      let finalUserId = +userid;
      let finalCustomerId = +customerid;
      let finalUsername = username;

      if (!finalUserId || !finalCustomerId) {
        try {
          const swxUser = JSON.parse(localStorage.getItem('user'));
          if (swxUser) {
            finalUserId = swxUser.id;
            finalCustomerId = swxUser.customer?.id || swxUser.customer_id;
            finalUsername = swxUser.email || swxUser.username;
          }
        } catch (e) {
          console.warn('Could not parse global user state from localStorage');
        }
      }

      this.setUserId(finalUserId || 0);
      this.setCustomerId(finalCustomerId || 0);
      this.setUsername(finalUsername || 'admin@shopworx');

      await this.fetchConfig();
      await this.allShifts();
      await this.getBusinessTime();
      await this.getMachines();
      await this.fetchDowntimeReasons();

      if (this.activeSite) {
        this.initialized = true;
      } else {
        console.log('[Component Log] Waiting for Shopworx shell to hydrate user session...');
      }

      this.loading = false;
    },

    async fetchConfig() {
      const configData = await this.getFileData('assets/config.json');
      if (configData && typeof configData === 'object' && !Array.isArray(configData)) {
        this.setConfigJson(configData);
      } else {
        this.setConfigJson({});
      }
    },

    onResize() {
      let winHeight = window.innerHeight;
      const winWidth = window.innerWidth;

      if (this.isTV || this.isFullscreen) {
        winHeight -= 64;
      } else {
        winHeight -= 112;
      }

      if (winWidth / this.ratio < winHeight - 40) {
        const height = winWidth / this.ratio;
        const width = height * this.ratio;
        this.width = width - 6;
        this.height = height;
      } else {
        const width = winHeight * this.ratio;
        const height = width / this.ratio;
        this.width = width - 6;
        this.height = height + 10;
      }

      this.$nextTick(() => {
        this.scale = this.width / 1100;
      });
    },

    setTime() {
      this.now = this.dateFormat();
    },

    dateFormat() {
      if (!this.serverTime) return '';
      const date = new Date(this.serverTime);
      const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
      const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
      const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
      const currentHours = date.getHours();
      const currentMin = date.getMinutes();
      const hour = (`0${currentHours}`).slice(-2);
      const min = (`0${currentMin}`).slice(-2);
      return `${day} ${month} ${year} | ${hour}:${min}`;
    },
  },
};
</script>

<style scoped>
.w-full {
  width: 100%;
}
.h-full {
  height: 100%;
}
</style>
